import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type { SelectProps } from 'components/common';
import { CommonSelect, PlaceholderNoAccess } from 'components/common';
import { type SelectFlowsParams, useInfiniteFlows } from '../api';
import type { Flow } from '../types';
import { useInView } from 'react-intersection-observer';
import { usePermissionGuard } from 'features/login';

interface SelectFlowsAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  params?: SelectFlowsParams;
}

type SelectFlowsProps = Partial<
  Pick<
    SelectProps,
    'handleChange' | 'name' | 'inputLabel' | 'onClose' | 'disabled' | 'asAutocomplete'
  > &
  SelectFlowsAttrs
>;

const SelectFlowsComponent: FC<SelectFlowsProps> = (
  {
    handleChange,
    name,
    inputLabel,
    multiples,
    placeholder,
    onClose,
    open,
    params,
    disabled,
    asAutocomplete,
  }) => {
  const { ref, inView } = useInView({
    threshold: 0,
  });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteFlows({ params });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const options = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.data.map(({ name, id }: Flow) => ({
        label: name,
        value: id,
      })),
    );
  }, [data]);

  return (
    <CommonSelect
      name={name ?? 'flows'}
      inputLabel={inputLabel ?? 'Flows'}
      options={options}
      handleChange={handleChange}
      placeholder={placeholder}
      multiples={multiples}
      onClose={onClose}
      open={open}
      loading={isFetchingNextPage}
      loadMoreRef={ref}
      disabled={disabled}
      asAutocomplete={asAutocomplete}
    />
  );
};

const SelectFlows: FC<SelectFlowsProps> = (props) => {
  const { accessToViewFlow } = usePermissionGuard();
  if (!accessToViewFlow)
    return <PlaceholderNoAccess title={'No access to view flow'} />;

  return <SelectFlowsComponent {...props} />;
};

export default SelectFlows;
